import { Box, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { useEffect, useMemo, useState } from "react";
import { ShadowDOMProvider } from "../../ui/shadow-dom-provider";
import { PatientOverviewV2Content } from "../patient-overview-v2-content";
import { OneMedicalFilterToggle } from "../patient-overview-v2-content/components/one-medical-filter-toggle";
import { CustomKeywordsModal } from "../shared-components/custom-keywords-modal";
import { DemographicsModal } from "../shared-components/demos-modal";
import { FailedConsolidatedPage } from "../shared-components/failed-consolidated-page";
import {
  getAiSummaryFromMappedConsolidated,
  SummaryContent,
} from "../shared-components/fhir-sections/summaries/content";
import { PatientHeader } from "../shared-components/header";
import { MrModal } from "../shared-components/mr-modal";
import { PatientForm } from "../shared-components/patient-form";
import { PatientMatches } from "../shared-components/patient-matches";
import { DocumentProgressContainer } from "../shared-components/progress-bar";
import { usePatientConsolidatedContext } from "../shared-logic/consolidated-context";
import {
  getValidPatientTab,
  PatientConsolidatedStateActionType,
  PatientQueryParamProps,
  PatientTab,
} from "../shared-logic/consolidated-context/reducer";
import { useEhrContext } from "../shared-logic/ehr-context/ehr-context";
import { useSharedContext } from "../shared-logic/shared-context";
import { useGetConsolidatedRQ } from "../shared-logic/useGetConsolidatedRQ";
import { useGetFacilities } from "../shared-logic/useGetFacilties";
import { useGetMedicalRecord } from "../shared-logic/useGetMedicalRecord";
import { useGetPatientInfo } from "../shared-logic/useGetPatientInfo";
import { useGetPatientMatches } from "../shared-logic/useGetPatientMatches";
import useMetriportToast from "../shared-logic/useMetriportToast";
import { usePatientHieOptOut } from "../shared-logic/usePatientHieOptOut";
import { useQueryDocuments } from "../shared-logic/useQueryDocuments";
import { useSubmitPatient } from "../shared-logic/useSubmitPatient";
import { ConsolidatedDataIsReadyToast } from "./consolidated-data-is-ready-toast";

interface PatientOverviewV2Props {
  patientId: string;
  patientQueryParams?: PatientQueryParamProps | undefined;
  isImpersonating?: boolean;
}

export function PatientOverviewV2({
  patientId,
  patientQueryParams,
  isImpersonating,
}: PatientOverviewV2Props) {
  const [isOneMedicalFilterEnabled, setIsOneMedicalFilterEnabled] = useState<boolean>(false);
  const [patientTab, setPatientTab] = useState<PatientTab>(
    getValidPatientTab(patientQueryParams?.patientTab)
  );
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  const patientFormDisclosure = useDisclosure();
  const patientMRDisclosure = useDisclosure();
  const patientDemosDisclosure = useDisclosure();
  const patientMatchesDisclosure = useDisclosure();
  const customKeywordsDisclosure = useDisclosure();

  const toast = useMetriportToast();

  const { sharedState } = useSharedContext();
  const ehrContext = useEhrContext();
  const isEhr = ehrContext.isEhr;

  const { patientConsolidatedDispatch, patientConsolidatedState } = usePatientConsolidatedContext();
  const { createOrUpdatePatient, isSubmitting } = useSubmitPatient({
    patientId,
    onComplete: () => patientFormDisclosure.onClose(),
  });
  const { patient, isFetchingPatient, setPatient } = useGetPatientInfo({ patientId });
  const { facilities } = useGetFacilities({ isDisabledOnLoad: isEhr });
  const { patientMatches } = useGetPatientMatches({ patientId });
  const { patientHieOptOut, toggleHieOptOut } = usePatientHieOptOut({
    patientId,
    isDisabledOnLoad: isEhr,
  });

  const documentsState = patientConsolidatedState[patientId]?.documents;
  const lastDocQueryState = patientConsolidatedState[patientId]?.lastDqStatus;
  const initialDqRunState = patientConsolidatedState[patientId]?.initialDqRun;
  const consolidatedState = patientConsolidatedState[patientId]?.consolidated;
  const lastConsolidatedRequestId = patientConsolidatedState[patientId]?.lastConsolidatedRequestId;
  const isManuallyRefreshingDq = patientConsolidatedState[patientId]?.isManuallyRefreshingDq;

  const { existingMrStatus, isMrDownloading, isAtLeastOneMrDownloading, onDownloadMR } =
    useGetMedicalRecord({ patientId });

  const {
    isQueryingDocuments,
    isQueryError,
    dqProgress,
    documentListResult,
    initialDocumentListResult,
    searchDocFilters,
    onQueryPatientDocuments,
    onSearchPatientDocuments,
    resetLastDocQueryStatus,
  } = useQueryDocuments({ patientId });

  const {
    consolidated,
    consolidatedResources,
    isQueryingConsolidated,
    onQueryPatientConsolidated,
    suspects,
    careGaps,
    isConsolidatedFailed,
  } = useGetConsolidatedRQ({ patientId });

  const displayBundle = ehrContext.consolidatedDiff ?? consolidated;

  const sdk = useMemo(() => {
    if (!displayBundle) return undefined;
    return FhirBundleSdk.createSync(displayBundle);
  }, [displayBundle]);

  useEffect(() => {
    if (!documentsState && searchDocFilters) {
      onSearchPatientDocuments({ ...searchDocFilters, setFiltersInStorage: false });
    }
  }, [documentsState, searchDocFilters]);

  useEffect(() => {
    async function initDq() {
      if (!initialDqRunState && initialDocumentListResult) {
        if (initialDocumentListResult.documents?.length === 0) await onQueryPatientDocuments();
        patientConsolidatedDispatch({
          type: PatientConsolidatedStateActionType.setPatientsInitialDqRun,
          patientId,
          initialDqRun: "initialized",
        });
      }
    }
    initDq();
  }, [initialDqRunState, initialDocumentListResult]);

  useEffect(() => {
    if (!lastConsolidatedRequestId && initialDqRunState && !isQueryingDocuments && !isQueryError) {
      onQueryPatientConsolidated();
    }
  }, [lastConsolidatedRequestId, initialDqRunState, isQueryingDocuments, isQueryError]);

  useEffect(() => {
    if (documentListResult) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsDocuments,
        patientId,
        documents: documentListResult.documents,
      });
    }
  }, [documentListResult]);

  useEffect(() => {
    if (consolidatedResources) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsConsolidated,
        patientId,
        consolidatedResources,
      });
    }
  }, [consolidatedResources, patientConsolidatedDispatch, patientId]);

  function setPatientTabAndParams(patientTab: PatientTab) {
    setPatientTab(patientTab);
    patientQueryParams?.setPatientQueryParams({ patientTab });
  }

  const isNotifyOnManualRefresh = !lastDocQueryState?.isProcessing && isManuallyRefreshingDq;

  useEffect(() => {
    const toastId = patientId;
    if (isNotifyOnManualRefresh) {
      toast.success({
        id: toastId,
        title: "Data is ready!",
        duration: null,
        render: ({ onClose }) => (
          <ConsolidatedDataIsReadyToast
            onRefreshClick={() => {
              onManualRefreshConsolidated();
              onClose();
            }}
          />
        ),
      });
    }
    return () => {
      toast.close(toastId);
    };
  }, [isNotifyOnManualRefresh]);

  function onManualRefreshConsolidated() {
    onQueryPatientConsolidated();
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.resetPatientsConsolidated,
      patientId,
    });
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setIsManuallyRefreshedDq,
      patientId,
      isManuallyRefreshingDq: false,
    });
  }

  async function onManualRefreshDocumentQuery() {
    await onQueryPatientDocuments();
    resetLastDocQueryStatus();
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setIsManuallyRefreshedDq,
      patientId,
      isManuallyRefreshingDq: true,
    });
  }

  return (
    <>
      {/* V1 Going to add Breadcrumbs */}
      <Box
        p={5}
        maxW={"2560px"}
        minH={"100vh"}
        mx={"auto"}
        bg={useColorModeValue("gray.100", "gray.900")}
      >
        <Box
          bg={useColorModeValue("gray.100", "gray.900")}
          position={isEhr ? "sticky" : "relative"}
          top={0}
          zIndex={5}
        >
          <PatientHeader
            patient={patient}
            isFetchingPatient={isFetchingPatient}
            isQueryingDocuments={isQueryingDocuments || isQueryingConsolidated}
            isAtLeastOneMrDownloading={isAtLeastOneMrDownloading}
            isHieOptedOut={patientHieOptOut}
            usingSectionSearch={false}
            lastDocQuery={lastDocQueryState?.lastDocQueryDate}
            actions={{
              onQueryPatientDocuments: async () => {
                await onManualRefreshDocumentQuery();
              },
              onGenerateMedicalRecord: patientMRDisclosure.onOpen,
              onEditPatient: patientFormDisclosure.onOpen,
              onDisplayPatientDemos: patientDemosDisclosure.onOpen,
              onViewPatientMatches: patientMatchesDisclosure.onOpen,
              onSetPatientTab: setPatientTabAndParams,
              toggleHieOptOut,
              onEditCustomKeywords: !isConsolidatedFailed
                ? customKeywordsDisclosure.onOpen
                : undefined,
              setUsingMetriportOnlyBundleFromConsolidated: ehrContext.toggleShowDiff,
            }}
            isFetchingDiff={ehrContext.isFetchingDiff}
            usingMetriportOnlyBundleFromConsolidated={ehrContext.isShowingDiff}
            isLimited={isEhr}
            setHeaderHeight={setHeaderHeight}
            selectedTab={patientTab}
          />
        </Box>
        {patientTab === "fhir" && !isConsolidatedFailed ? (
          <ShadowDOMProvider>
            <PatientOverviewV2Content
              fhirSdk={sdk}
              hasAiSummary={!!getAiSummaryFromMappedConsolidated(consolidatedState)}
              isLoadingData={isQueryingConsolidated || !consolidatedState}
              isImpersonating={isImpersonating ?? false}
              headerHeight={headerHeight}
              suspects={suspects}
              careGaps={careGaps}
              isOneMedicalFilterEnabled={isOneMedicalFilterEnabled}
              consolidated={displayBundle}
              patientId={patientId}
              isEhr={isEhr}
            >
              <SummaryContent mappedConsolidated={consolidatedState} />
            </PatientOverviewV2Content>
            {/* One Medical Filter Toggle - Only for V2 */}
            {sharedState.isAmazonUser && (
              <OneMedicalFilterToggle
                isEnabled={isOneMedicalFilterEnabled}
                onToggle={setIsOneMedicalFilterEnabled}
              />
            )}
            <CustomKeywordsModal
              isOpen={customKeywordsDisclosure.isOpen}
              onClose={customKeywordsDisclosure.onClose}
            />
          </ShadowDOMProvider>
        ) : (
          <FailedConsolidatedPage />
        )}
      </Box>
      <DocumentProgressContainer dqProgress={dqProgress} />
      <PatientMatches
        isOpen={patientMatchesDisclosure.isOpen || patientTab === "matches"}
        patient={patient}
        patientMatches={patientMatches}
        actions={{
          onClose: () => {
            setPatientTabAndParams("fhir");
            patientMatchesDisclosure.onClose();
          },
        }}
      />
      {patientFormDisclosure.isOpen && (
        <PatientForm
          isOpen={patientFormDisclosure.isOpen}
          selectedPatient={patient}
          facilities={facilities}
          isSubmitting={isSubmitting}
          actions={{
            onCloseForm: patientFormDisclosure.onClose,
            onCreateOrUpdatePatient: async patient => {
              const resp = await createOrUpdatePatient(patient);
              setPatient(resp);
            },
          }}
        />
      )}
      <MrModal
        isOpen={patientMRDisclosure.isOpen}
        isDownloading={isMrDownloading}
        existingMrStatus={existingMrStatus}
        actions={{
          onCloseModal: patientMRDisclosure.onClose,
          onDownloadMR,
        }}
      />
      <DemographicsModal
        isOpen={patientDemosDisclosure.isOpen}
        patient={patient}
        lastDocQuery={lastDocQueryState?.lastDocQueryDate}
        patientMatches={patientMatches}
        actions={{
          onCloseModal: patientDemosDisclosure.onClose,
        }}
      />
    </>
  );
}
